import {
  Component, AfterViewInit, OnDestroy,
  ElementRef, ViewChild, NgZone, inject,
} from '@angular/core';

@Component({
  selector: 'app-africa-map',
  standalone: true,
  imports: [],
  template: `
    <div
      #mapContainer
      class="relative h-full w-full rounded-3xl overflow-hidden glass"
      style="border-color:rgba(0,140,240,0.16)"
    >
      <!-- Fine grid backdrop -->
      <div
        class="absolute inset-0"
        style="
          background-image:
            linear-gradient(rgba(0,140,240,0.04) 1px,transparent 1px),
            linear-gradient(90deg,rgba(0,140,240,0.04) 1px,transparent 1px);
          background-size:28px 28px
        "
      ></div>

      <svg
        viewBox="0 0 360 440"
        class="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Map of Africa showing Aurrica operating cities"
        [style.transform]="zoomed ? 'scale(1.72)' : 'scale(1)'"
        style="overflow:visible;transition:transform 1.6s cubic-bezier(0.34,1.12,0.64,1);transform-origin:74% 54%"
      >
        <defs>
          <filter id="continent-glow" x="-5%" y="-5%" width="110%" height="110%">
            <feGaussianBlur stdDeviation="3.5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <radialGradient id="halo-major" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stop-color="#00D4FF" stop-opacity="0.35"/>
            <stop offset="100%" stop-color="#00D4FF" stop-opacity="0"/>
          </radialGradient>
          <radialGradient id="halo-minor" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stop-color="#008CF0" stop-opacity="0.28"/>
            <stop offset="100%" stop-color="#008CF0" stop-opacity="0"/>
          </radialGradient>
        </defs>

        <!-- ── Accurate continent outline (clockwise from NW Morocco) ── -->
        <path
          d="
            M 71,13
            C 94,10 115,8 143,5
            C 165,20 188,38 215,35
            C 232,32 248,37 262,48
            C 274,70 290,106 302,130
            C 309,143 312,151 315,157
            C 332,156 345,154 355,155
            C 348,168 338,190 326,214
            C 321,226 318,234 300,250
            C 298,264 298,278 300,286
            C 300,300 305,310 303,316
            C 293,328 281,340 274,344
            C 268,358 263,370 263,380
            C 260,390 256,400 255,404
            C 243,416 227,428 210,430
            C 200,432 192,432 184,426
            C 176,418 173,406 172,380
            C 171,366 172,360 172,355
            C 170,338 168,308 166,278
            C 164,268 162,260 159,254
            C 154,242 150,232 147,224
            C 147,218 148,212 148,202
            C 143,198 136,197 130,197
            C 122,193 117,187 110,186
            C 103,189 96,196 90,199
            C 80,203 68,205 62,201
            C 50,194 38,178 32,169
            C 22,159 14,148 13,138
            C 12,124 16,106 20,87
            C 28,68 40,54 52,44
            C 59,34 65,22 71,13 Z
          "
          fill="rgba(0,140,240,0.065)"
          stroke="rgba(0,140,240,0.42)"
          stroke-width="1.4"
          stroke-linejoin="round"
          filter="url(#continent-glow)"
        />

        <!-- ── Route lines ── -->
        <line x1="251" y1="237" x2="263" y2="224"
          stroke="#008CF0" stroke-width="1" stroke-dasharray="5 4"
          style="animation:dash-flow 2.2s linear infinite"/>
        <line x1="251" y1="237" x2="284" y2="234"
          stroke="#008CF0" stroke-width="1" stroke-dasharray="5 4"
          style="animation:dash-flow 2.8s linear infinite"/>
        <line x1="284" y1="234" x2="299" y2="250"
          stroke="#00D4FF" stroke-width="1" stroke-dasharray="4 3"
          style="animation:dash-flow 1.8s linear infinite"/>
        <line x1="251" y1="237" x2="296" y2="266"
          stroke="#008CF0" stroke-width="1" stroke-dasharray="5 4"
          style="animation:dash-flow 3.2s linear infinite"/>
        <line x1="284" y1="234" x2="296" y2="266"
          stroke="#008CF0" stroke-width="0.8" stroke-dasharray="4 4"
          style="animation:dash-flow 2.5s linear infinite"/>

        <!-- ── City: Kigali (HQ) ── -->
        <circle cx="251" cy="237" r="26" fill="url(#halo-major)"/>
        <circle cx="251" cy="237" r="13" fill="none" stroke="#00D4FF" stroke-width="1.5"
          [class.node-zoom]="zoomed"
          style="animation:city-pulse 2.4s ease-out infinite"/>
        <circle cx="251" cy="237" r="13" fill="none" stroke="#00D4FF" stroke-width="1"
          [class.node-zoom]="zoomed"
          style="animation:city-pulse 2.4s ease-out 1.2s infinite"/>
        <circle cx="251" cy="237" r="5" fill="#00D4FF"
          style="filter:drop-shadow(0 0 6px #00D4FF)"/>
        <text x="259" y="233" fill="rgba(176,196,222,0.85)"
          font-size="9.5" font-family="Inter,system-ui,sans-serif" font-weight="600">Kigali</text>
        <text x="259" y="243" fill="rgba(0,212,255,0.65)"
          font-size="7.5" font-family="Inter,system-ui,sans-serif">HQ</text>

        <!-- ── City: Kampala ── -->
        <circle cx="263" cy="224" r="18" fill="url(#halo-minor)"/>
        <circle cx="263" cy="224" r="10" fill="none" stroke="#008CF0" stroke-width="1.2"
          style="animation:city-pulse-sm 2.8s ease-out 0.6s infinite"/>
        <circle cx="263" cy="224" r="4" fill="#008CF0"
          style="filter:drop-shadow(0 0 5px #008CF0)"/>
        <text x="269" y="222" fill="rgba(176,196,222,0.75)"
          font-size="8.5" font-family="Inter,system-ui,sans-serif" font-weight="500">Kampala</text>

        <!-- ── City: Nairobi ── -->
        <circle cx="284" cy="234" r="20" fill="url(#halo-minor)"/>
        <circle cx="284" cy="234" r="11" fill="none" stroke="#008CF0" stroke-width="1.2"
          style="animation:city-pulse-sm 3s ease-out 1s infinite"/>
        <circle cx="284" cy="234" r="4.5" fill="#008CF0"
          style="filter:drop-shadow(0 0 5px #008CF0)"/>
        <text x="291" y="232" fill="rgba(176,196,222,0.75)"
          font-size="8.5" font-family="Inter,system-ui,sans-serif" font-weight="500">Nairobi</text>

        <!-- ── City: Mombasa ── -->
        <circle cx="299" cy="250" r="15" fill="url(#halo-minor)"/>
        <circle cx="299" cy="250" r="8" fill="none" stroke="#008CF0" stroke-width="1"
          style="animation:city-pulse-sm 2.6s ease-out 0.3s infinite"/>
        <circle cx="299" cy="250" r="3.5" fill="#008CF0"/>
        <text x="305" y="249" fill="rgba(176,196,222,0.65)"
          font-size="8" font-family="Inter,system-ui,sans-serif">Mombasa</text>

        <!-- ── City: Dar es Salaam ── -->
        <circle cx="296" cy="266" r="16" fill="url(#halo-minor)"/>
        <circle cx="296" cy="266" r="9" fill="none" stroke="#008CF0" stroke-width="1"
          style="animation:city-pulse-sm 3.2s ease-out 0.8s infinite"/>
        <circle cx="296" cy="266" r="3.5" fill="#008CF0"/>
        <text x="302" y="265" fill="rgba(176,196,222,0.65)"
          font-size="8" font-family="Inter,system-ui,sans-serif">Dar es Salaam</text>
      </svg>

      <!-- HQ badge -->
      <div
        class="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold glass"
        style="color:#00D4FF;border-color:rgba(0,212,255,0.28);z-index:10"
      >
        <span class="w-1.5 h-1.5 rounded-full" style="background:#00D4FF;box-shadow:0 0 6px #00D4FF"></span>
        HQ · Kigali, Rwanda
      </div>
    </div>
  `,
  styles: [
    `:host { display:block; height:100%; }`,
    `.node-zoom { animation-duration: 1.6s !important; }`,
  ],
})
export class AfricaMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer') containerRef!: ElementRef;
  zoomed = false;
  private observer?: IntersectionObserver;
  private zone = inject(NgZone);

  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.zone.run(() => {
          if (entry.isIntersecting) {
            setTimeout(() => { this.zoomed = true; }, 400);
          } else {
            this.zoomed = false;
          }
        });
      },
      { threshold: 0.25 },
    );
    this.observer.observe(this.containerRef.nativeElement);
  }

  ngOnDestroy(): void { this.observer?.disconnect(); }
}
